/**
 * El renderer ÚNICO de cualquier reporte tabular del ERP.
 *
 * El problema que resuelve
 * ------------------------
 * El backend calculaba cinco cuadros —mayor, balance de sumas y saldos, estado
 * de resultados, libro de IVA ventas y libro de IVA compras— y ninguno llegaba a
 * la pantalla. La salida fácil habría sido escribir cinco componentes, uno por
 * reporte, como ya pasó con `AgingResultDialog` para la cuenta corriente; el
 * sexto reporte habría traído el sexto componente.
 *
 * Acá no hay una línea que sepa de contabilidad. El backend manda el cuadro con
 * una forma común (`nucleo.core.tabular.ReporteTabular`): título, pares
 * etiqueta/valor del encabezado, columnas, secciones de filas y totales. Este
 * componente dibuja esa forma. Un reporte nuevo, de cualquiera de los 13
 * módulos, aparece dibujado sin tocar el frontend.
 *
 * Tres cosas que sí decide este archivo, y sólo tres
 * --------------------------------------------------
 * 1. **El formato de los números en pantalla**, con el mismo helper de moneda que
 *    el resto de la app (`formatCurrency`). En el PDF el texto lo pone Python;
 *    acá lo pone el navegador, y los dos dicen lo mismo porque el criterio
 *    —es-AR, dos decimales— es el mismo.
 * 2. **Que una fila lleve a su registro**: el backend manda `link` y acá se
 *    dibuja como enlace. Un reporte del que no se puede saltar al asiento que
 *    explica el renglón es un callejón sin salida.
 * 3. **Que las advertencias del reporte se vean**. `notes` no es decoración: es
 *    donde el libro de IVA avisa que hay impuestos sin clasificar y donde el
 *    balance avisa que no cuadra. Se dibujan destacadas y nunca se ocultan.
 */

import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { formatCurrency, formatDate } from '@/components/fields/format';
import { recordPath } from '@/components/shell/menu-utils';
import { Button } from '@/components/shell/primitives';
import type {
  ActionResult,
  JsonValue,
  TabularColumn,
  TabularReportData,
  TabularRow,
} from '@/lib/types';

// ---------------------------------------------------------------------------
// Lectura de la respuesta
// ---------------------------------------------------------------------------
/**
 * Extrae el cuadro de la respuesta de una acción, o `null` si no trajo ninguno.
 *
 * Se detecta por la CLAVE `table` y por su forma, no por el nombre de la acción:
 * así lo cubren los cinco reportes contables y cualquier otro que el backend
 * agregue, sin acoplar el frontend a nombres técnicos. Se lee con desconfianza:
 * cualquier forma inesperada devuelve `null` y la acción se comporta como
 * cualquier otra (toast con el mensaje del servidor).
 */
export function leerReporteTabular(resultado: ActionResult | undefined): TabularReportData | null {
  if (resultado === undefined) return null;
  const resultados = (resultado as { results?: unknown }).results;
  if (!Array.isArray(resultados) || resultados.length === 0) return null;
  const datos = (resultados[0] as { data?: unknown }).data;
  if (datos === null || typeof datos !== 'object') return null;
  return leerCuadro((datos as { table?: unknown }).table);
}

/**
 * Un cuadro suelto, si tiene la forma de uno. Lo usa también el cuadro de una
 * tarjeta de la ficha (`api.smartButtonTable`), que lo trae en `table` sin el
 * envoltorio de una acción.
 */
export function leerCuadro(cuadro: unknown): TabularReportData | null {
  if (cuadro === null || cuadro === undefined || typeof cuadro !== 'object') return null;
  const t = cuadro as Record<string, unknown>;
  if (typeof t.title !== 'string' || !Array.isArray(t.columns) || !Array.isArray(t.sections)) {
    return null;
  }
  return cuadro as TabularReportData;
}

// ---------------------------------------------------------------------------
// Formato de celda
// ---------------------------------------------------------------------------
/** Cómo se escribe el valor de una celda según el tipo de su columna. */
function textoDeCelda(valor: JsonValue | undefined, columna: TabularColumn): string {
  if (valor === null || valor === undefined || valor === '') return '';
  switch (columna.kind) {
    case 'monetary':
    case 'number':
      // Los importes llegan como STRING decimal, no como número: el backend no
      // los pasa por `float` y el frontend tampoco debe hacerlo.
      return formatCurrency(valor as string);
    case 'integer':
      return new Intl.NumberFormat('es-AR').format(Number(valor));
    case 'date':
      return formatDate(String(valor));
    default:
      return String(valor);
  }
}

const CLASE_ALINEACION: Record<string, string> = {
  left: 'text-left',
  right: 'text-right tabular-nums',
  center: 'text-center',
};

/** Cuánto pesa una fila. El total general pisa; un subtotal sólo separa. */
const CLASE_ENFASIS: Record<string, string> = {
  '': '',
  subtotal: 'bg-app-subtle font-semibold',
  total: 'bg-app-subtle font-bold border-t-2 border-line',
  muted: 'text-fg-faint',
};

function Fila({
  fila,
  columnas,
  extraClase = '',
}: {
  fila: TabularRow;
  columnas: TabularColumn[];
  extraClase?: string;
}) {
  const enlace = fila.link;
  return (
    <tr className={`border-b border-line last:border-0 ${CLASE_ENFASIS[fila.emphasis] ?? ''} ${extraClase}`}>
      {columnas.map((columna, indice) => {
        const texto = textoDeCelda(fila.values[columna.key], columna);
        // El enlace va en la PRIMERA celda con texto y no en toda la fila: una
        // fila entera clickeable impide seleccionar un importe para copiarlo, y
        // copiar un número de un balance es media razón por la que se lo mira.
        const primeraConTexto =
          enlace !== null && texto !== '' && columnas.slice(0, indice).every((c) => textoDeCelda(fila.values[c.key], c) === '');
        return (
          <td
            key={columna.key}
            className={`px-3 py-1.5 ${CLASE_ALINEACION[columna.align] ?? 'text-left'}`}
          >
            {primeraConTexto && enlace !== null ? (
              <Link to={recordPath(enlace.model, enlace.id)} className="text-accent hover:underline">
                {texto}
              </Link>
            ) : (
              texto
            )}
          </td>
        );
      })}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// El cuadro
// ---------------------------------------------------------------------------
export interface TabularReportProps {
  datos: TabularReportData;
  /** Botones propios de la pantalla que lo contiene (p. ej. «Descargar PDF»). */
  acciones?: React.ReactNode;
  /** Sin encabezado: lo dibuja la pantalla que lo contiene. */
  sinEncabezado?: boolean;
}

export function TabularReport({ datos, acciones, sinEncabezado = false }: TabularReportProps) {
  const vacio = datos.sections.every((seccion) => seccion.rows.length === 0);

  return (
    <div className="flex flex-col gap-4">
      {!sinEncabezado && (
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{datos.title}</h2>
            {datos.subtitle !== '' && <p className="text-sm text-fg-muted">{datos.subtitle}</p>}
          </div>
          {acciones}
        </header>
      )}

      {datos.meta.length > 0 && (
        <dl className="flex flex-wrap gap-x-6 gap-y-1 rounded-lg border border-line bg-app-subtle px-3 py-2 text-sm">
          {datos.meta.map(([etiqueta, valor]) => (
            <div key={etiqueta} className="flex gap-1.5">
              <dt className="text-fg-muted">{etiqueta}:</dt>
              <dd className="font-medium">{valor}</dd>
            </div>
          ))}
        </dl>
      )}

      {/* Las advertencias van ARRIBA del cuadro: un balance que no cuadra o un
          libro con impuestos sin clasificar tiene que decirlo antes de que
          alguien copie el número, no en una nota al pie. */}
      {datos.notes.length > 0 && (
        <ul className="space-y-1 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-sm">
          {datos.notes.map((nota) => (
            <li key={nota}>{nota}</li>
          ))}
        </ul>
      )}

      {vacio ? (
        <p className="rounded-lg border border-dashed border-line px-3 py-8 text-center text-sm text-fg-muted">
          {datos.empty_label}
        </p>
      ) : (
        // El cuadro se desplaza a lo ancho por su cuenta: un libro de IVA con
        // una columna por alícuota no entra en una pantalla y no puede hacer que
        // se desplace la página entera.
        <div className="overflow-x-auto rounded-lg border border-line">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">{datos.title}</caption>
            <thead>
              <tr className="border-b border-line bg-app-subtle">
                {datos.columns.map((columna) => (
                  <th
                    key={columna.key}
                    scope="col"
                    className={`whitespace-nowrap px-3 py-2 font-medium text-fg-muted ${CLASE_ALINEACION[columna.align] ?? 'text-left'}`}
                  >
                    {columna.label}
                  </th>
                ))}
              </tr>
            </thead>
            {datos.sections.map((seccion, indice) => (
              <tbody key={seccion.label !== '' ? seccion.label : `seccion-${indice}`}>
                {seccion.label !== '' && (
                  <tr className="border-b border-line bg-app-subtle">
                    <th
                      scope="colgroup"
                      colSpan={datos.columns.length}
                      className="px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wide"
                    >
                      {seccion.label}
                    </th>
                  </tr>
                )}
                {seccion.rows.map((fila, i) => (
                  <Fila key={`${indice}-${i}`} fila={fila} columnas={datos.columns} />
                ))}
                {seccion.total !== null && (
                  <Fila fila={seccion.total} columnas={datos.columns} />
                )}
              </tbody>
            ))}
            {datos.total !== null && (
              <tfoot>
                <Fila fila={datos.total} columnas={datos.columns} />
              </tfoot>
            )}
          </table>
        </div>
      )}

      {datos.summary !== '' && <p className="text-xs text-fg-faint">{datos.summary}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// El mismo cuadro, como panel modal
// ---------------------------------------------------------------------------
export interface TabularReportDialogProps {
  /** Título de la acción que lo produjo. El del cuadro va adentro. */
  titulo: string;
  datos: TabularReportData | null;
  /** Botón de PDF y demás, si la pantalla que abre el panel los ofrece. */
  acciones?: React.ReactNode;
  onClose: () => void;
}

/**
 * El cuadro sobre un formulario: es lo que ve quien aprieta «Ver mayor» desde la
 * ficha de una cuenta o «Libro de IVA ventas» desde la de la empresa.
 *
 * Mismo comportamiento de teclado y foco que el resto de los diálogos del ERP
 * (Escape cierra, el foco vuelve a donde estaba, el fondo no se desplaza).
 */
export function TabularReportDialog({ titulo, datos, acciones, onClose }: TabularReportDialogProps) {
  const panel = useRef<HTMLDivElement>(null);
  const focoPrevio = useRef<HTMLElement | null>(null);
  const abierto = datos !== null;

  useEffect(() => {
    if (!abierto) return;
    focoPrevio.current = document.activeElement as HTMLElement | null;
    const alTeclear = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', alTeclear, true);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const temporizador = window.setTimeout(() => {
      panel.current?.querySelector<HTMLElement>('#reporte-cerrar')?.focus();
    }, 0);
    return () => {
      document.removeEventListener('keydown', alTeclear, true);
      document.body.style.overflow = overflow;
      window.clearTimeout(temporizador);
      focoPrevio.current?.focus();
    };
  }, [abierto, onClose]);

  if (datos === null) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
      <div className="absolute inset-0 bg-fg/30 animate-fade-in" onClick={onClose} aria-hidden="true" />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reporte-titulo"
        className="relative flex max-h-[88svh] w-full max-w-5xl flex-col rounded-xl border border-line bg-surface p-5 shadow-lg animate-slide-up"
      >
        <h2 id="reporte-titulo" className="sr-only">
          {titulo}
        </h2>
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <TabularReport datos={datos} acciones={acciones} />
        </div>
        <div className="mt-4 flex justify-end gap-2 border-t border-line pt-4">
          <Button variant="primary" onClick={onClose} id="reporte-cerrar">
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}
